import { useEffect, useContext, useState } from "react";
import { UserContext } from "../../../../context/UserContext";
import {
  getEntertainmentVideosByOwner,
  deleteEntertainmentVideo,
} from "../../../../api/entertainmentVideo.api";
import OwnerLayout from "../../../layout/OwnerLayout";
import { baseURL } from "../../../../api/api";
import { BsTrash3 } from "react-icons/bs";
import { Link } from "react-router-dom";
import {
  confirmMediaDelete,
  errorMsg,
  success,
} from "../../../../utils/notifications";
import { FiPlus, FiCalendar, FiMapPin, FiFilter } from "react-icons/fi";
import { RotatingLines } from "react-loader-spinner";
import EmptyState from "../../../../utils/EmptyState";
import { format } from "date-fns";

function EntertainmentVideos() {
  const { user } = useContext(UserContext);
  const [entertainmentVideos, setEntertainmentVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (user?._id) {
      fetchVideos();
    }
  }, [user?._id, filter]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const res = await getEntertainmentVideosByOwner(user._id);
      setEntertainmentVideos(res?.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      errorMsg("Error", "Failed to load videos");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      confirmMediaDelete().then(async (result) => {
        if (result.isConfirmed) {
          await deleteEntertainmentVideo(id);
          setEntertainmentVideos(
            entertainmentVideos.filter((video) => video._id !== id)
          );
          success("Success!", "Video deleted successfully!");
        }
      });
    } catch (error) {
      console.error("Error deleting video:", error);
      errorMsg("Oops", error?.response?.message || "Failed to delete video");
    }
  };

  const filteredVideos = entertainmentVideos.filter((video) => {
    // Filter by category
    if (filter !== "all" && video.category !== filter) return false;

    // Filter by search query
    if (
      searchQuery &&
      !video.name.toLowerCase().includes(searchQuery.toLowerCase())
    ) {
      return false;
    }

    return true;
  });

  const formatDate = (dateString) => {
    return format(new Date(dateString), "MMM dd, yyyy");
  };

  return (
    <OwnerLayout title="My Infotainment Videos">
      <div className="p-5">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">My Videos</h1>
            <p className="text-gray-600">
              {entertainmentVideos.length} videos uploaded
            </p>
          </div>

          <Link
            to="/infotainmentvideos/add"
            className="flex items-center justify-center rounded-md bg-yellow px-4 py-2.5 text-base font-semibold text-white shadow-sm hover:bg-yellow transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-500"
          >
            <FiPlus className="mr-2" />
            Upload New Video
          </Link>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search videos..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none transition"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute left-3 top-2.5 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <FiFilter className="text-gray-500 flex-shrink-0" />
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none transition appearance-none bg-white pr-8"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">All Categories</option>
                <option value="Entertainment">Entertainment</option>
                <option value="Information">Information</option>
              </select>
              <div className="pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <RotatingLines
              strokeColor="black"
              strokeWidth="5"
              animationDuration="0.75"
              width="96"
              visible={true}
            />
          </div>
        ) : filteredVideos.length === 0 ? (
          <EmptyState
            title="No videos found"
            description={
              searchQuery || filter !== "all"
                ? "Try adjusting your search or filter criteria"
                : "You haven't uploaded any videos yet"
            }
            action={
              <Link
                to="/infotainmentvideos/add"
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow hover:bg-yellow focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                <FiPlus className="mr-2" />
                Upload Your First Video
              </Link>
            }
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVideos.map((video) => (
              <div
                key={video._id}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
              >
                <div className="relative">
                  <video
                    className="w-full h-48 object-cover"
                    alt={video.name}
                    muted
                    controlsList="nodownload"
                  >
                    <source
                      src={`${baseURL}/s3/images/${video?.video?.name}`}
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                  <div className="absolute top-3 right-3 flex gap-2">
                    <button
                      onClick={(e) => handleDelete(video._id, e)}
                      className="bg-red-600 text-white p-3 rounded-full transition"
                      title="Delete"
                    >
                      <BsTrash3 size={14} />
                    </button>
                  </div>
                </div>

                <div className="p-4">
                  <Link to={`/infotainmentvideos/${video._id}`}>
                    <h3 className="font-semibold text-lg text-gray-800 mb-2 hover:text-yellow-600 transition">
                      {video.name}
                    </h3>
                  </Link>

                  <div className="space-y-3 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <FiCalendar className="text-gray-400" />
                      <span>
                        {formatDate(video.startDate)} -{" "}
                        {formatDate(video.endDate)}
                      </span>
                    </div>

                    {video.location && (
                      <div className="flex items-center gap-2">
                        <FiMapPin className="text-gray-400" />
                        <span>{video.location}</span>
                      </div>
                    )}

                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          video.category === "Entertainment"
                            ? "bg-purple-100 text-purple-800"
                            : video.category === "Education"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {video.category}
                      </span>

                      <Link
                        to={`/infotainmentvideos/${video._id}`}
                        className="text-yellow-600 hover:text-yellow-700 text-sm font-medium"
                      >
                        View Details →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </OwnerLayout>
  );
}

export default EntertainmentVideos;
